import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, CardActionArea, useTheme, CircularProgress, Chip } from '@mui/material';
import PageSalesCta from '../components/PageSalesCta';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';

const BlogSingle = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await apiService.getBlogBySlug(slug);
        if (res.success && res.post) setPost(res.post);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchPost();
  }, [slug]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (!post) return <Box sx={{ textAlign: 'center', py: 8 }}><Typography>Post not found.</Typography><Link to="/blogs">Back to Blogs</Link></Box>;

  return (
    <Box sx={{ minHeight: '100vh', py: 6, background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)' }}>
      <Container maxWidth="md">
        <Typography component="h1" variant="h3" gutterBottom sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          {post.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {post.author && <Chip size="small" icon={<PersonIcon />} label={post.author} />}
          {post.published_at && <Chip size="small" icon={<CalendarTodayIcon />} label={new Date(post.published_at).toLocaleDateString()} />}
        </Box>
        {post.featured_image && <Box component="img" src={post.featured_image} alt="" sx={{ width: '100%', borderRadius: 2, mb: 3, maxHeight: 400, objectFit: 'cover' }} />}
        <Box sx={{ '& p': { mb: 2 }, '& img': { maxWidth: '100%' } }} dangerouslySetInnerHTML={{ __html: post.content || '' }} />
        <Box sx={{ mt: 4 }}><Link to="/blogs" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>← Back to Our Blogs</Link></Box>
      </Container>
      <PageSalesCta />
    </Box>
  );
};

const BlogsList = () => {
  const theme = useTheme();
  const [data, setData] = useState({ posts: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.getBlogs().then(setData).finally(() => setLoading(false));
  }, []);

  const posts = data.posts || [];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ minHeight: '100vh', py: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)' }}>
      <Container maxWidth="lg">
        <Typography variant="h2" textAlign="center" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 2 }}>
          Our Blogs
        </Typography>
        <Typography variant="h6" textAlign="center" sx={{ mb: 6, color: 'text.secondary', maxWidth: 640, mx: 'auto', lineHeight: 1.65 }}>
          Practical notes on software, AI, and running tech in Kenya. Reading is free — when you are ready to build, we are
          one message away.
        </Typography>
        <Grid container spacing={4}>
          {posts.length === 0 && <Grid item xs={12}><Typography textAlign="center" color="text.secondary">No posts yet. Check back soon.</Typography></Grid>}
          {posts.map((p, i) => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea component={Link} to={`/blogs/${p.slug}`} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                    {p.featured_image && <Box component="img" src={p.featured_image} alt="" sx={{ height: 180, width: '100%', objectFit: 'cover' }} />}
                    <CardContent sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{p.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{p.excerpt || ''}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {p.author && <Chip size="small" label={p.author} />}
                        {p.published_at && <Chip size="small" label={new Date(p.published_at).toLocaleDateString()} />}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
      <PageSalesCta />
    </Box>
  );
};

const Blogs = () => {
  const { slug } = useParams();
  if (slug) return <BlogSingle />;
  return <BlogsList />;
};

export default Blogs;
